<template>
  <div class="relative">
    <!-- Notification Bell Button -->
    <button
      ref="triggerRef"
      type="button"
      class="relative p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-all duration-200"
      :class="{
        'text-red-600 dark:text-red-400 hover:text-red-700': store.hasExpired,
        'text-amber-600 dark:text-amber-400 hover:text-amber-700': !store.hasExpired && store.hasExpiringSoon,
      }"
      :title="tooltipText"
      @click="toggleDropdown"
      aria-label="Document Expiration Notifications"
    >
      <Bell class="w-5 h-5 transition-transform group-hover:scale-110" />

      <!-- Notification Badge -->
      <span
        v-if="store.hasAlerts"
        class="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] px-1 items-center justify-center rounded-full text-[10px] font-extrabold shadow-sm ring-2 ring-white dark:ring-gray-800"
        :class="store.hasExpired ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'"
      >
        <!-- Ping animation for expired critical alerts -->
        <span
          v-if="store.hasExpired"
          class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
        ></span>
        <span class="relative">{{ badgeCount }}</span>
      </span>
    </button>

    <!-- Dropdown Popover -->
    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="absolute right-0 z-50 mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] origin-top-right rounded-2xl bg-white dark:bg-gray-800 py-0 shadow-2xl ring-1 ring-black/5 dark:ring-white/10 border border-gray-200/80 dark:border-gray-700 focus:outline-none overflow-hidden"
      >
        <!-- Dropdown Header -->
        <div class="p-3.5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800/80 border-b border-gray-200/80 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <div
              class="w-7 h-7 rounded-lg flex items-center justify-center"
              :class="store.hasExpired ? 'bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400' : (store.hasExpiringSoon ? 'bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400')"
            >
              <Bell class="w-4 h-4" />
            </div>
            <div>
              <h3 class="text-xs font-bold text-gray-900 dark:text-white leading-tight">
                Document Expiration Alerts
              </h3>
              <p class="text-[10px] text-gray-500 dark:text-gray-400">
                {{ userRoleLabel }}
              </p>
            </div>
          </div>

          <!-- Quick Refresh Button -->
          <button
            type="button"
            class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700 transition"
            title="Refresh alerts"
            :disabled="store.isLoading"
            @click.stop="handleRefresh"
          >
            <RefreshCw class="w-3.5 h-3.5" :class="{ 'animate-spin': store.isLoading }" />
          </button>
        </div>

        <!-- Summary & Filter Chips (If alerts exist) -->
        <div v-if="store.hasAlerts" class="px-3 pt-2.5 pb-2 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between gap-1.5">
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="px-2.5 py-1 rounded-lg text-[11px] font-bold transition"
              :class="activeFilter === 'all' ? 'bg-secondary text-white shadow-xs' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200/60 dark:hover:bg-gray-700'"
              @click="activeFilter = 'all'"
            >
              All ({{ store.summary.total_alerts }})
            </button>

            <button
              v-if="store.summary.expired_count > 0"
              type="button"
              class="px-2 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1"
              :class="activeFilter === 'expired' ? 'bg-red-500 text-white shadow-xs' : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40'"
              @click="activeFilter = 'expired'"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-red-500" :class="{ 'bg-white': activeFilter === 'expired' }"></span>
              <span>Expired ({{ store.summary.expired_count }})</span>
            </button>

            <button
              v-if="store.summary.expiring_soon_count > 0"
              type="button"
              class="px-2 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1"
              :class="activeFilter === 'expiring_soon' ? 'bg-amber-500 text-white shadow-xs' : 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40'"
              @click="activeFilter = 'expiring_soon'"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-amber-500" :class="{ 'bg-white': activeFilter === 'expiring_soon' }"></span>
              <span>Expiring ({{ store.summary.expiring_soon_count }})</span>
            </button>
          </div>
        </div>

        <!-- Alert Items List -->
        <div class="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
          <!-- Loading State -->
          <div v-if="store.isLoading && !store.alerts.length" class="p-6 text-center space-y-2">
            <Loader2 class="w-6 h-6 animate-spin text-secondary mx-auto" />
            <p class="text-xs text-gray-500 dark:text-gray-400">Checking document expirations...</p>
          </div>

          <!-- Empty State (No Alerts) -->
          <div v-else-if="!filteredAlerts.length" class="p-6 text-center space-y-2">
            <div class="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
              <CheckCircle2 class="w-6 h-6" />
            </div>
            <p class="text-xs font-bold text-gray-800 dark:text-gray-200">
              {{ activeFilter === 'all' ? 'All documents up to date!' : (activeFilter === 'expired' ? 'No expired documents.' : 'No documents expiring soon.') }}
            </p>
            <p class="text-[11px] text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
              {{ activeFilter === 'all' ? 'There are no expired or expiring documents that require renewal attention.' : 'Check back later for renewal updates.' }}
            </p>
          </div>

          <!-- Alert Row Item -->
          <div
            v-for="alert in filteredAlerts"
            :key="alert.id"
            class="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer flex items-start gap-3 group"
            @click="handleAlertClick(alert)"
          >
            <!-- Status Icon -->
            <div
              class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 shadow-xs"
              :class="alert.is_expired ? 'bg-red-100 text-red-600 dark:bg-red-950/70 dark:text-red-400' : 'bg-amber-100 text-amber-600 dark:bg-amber-950/70 dark:text-amber-400'"
            >
              <AlertTriangle v-if="alert.is_expired" class="w-4 h-4" />
              <Clock v-else class="w-4 h-4" />
            </div>

            <!-- Details -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between gap-1">
                <p class="text-xs font-bold text-gray-900 dark:text-white truncate group-hover:text-secondary dark:group-hover:text-secondary-dark-text transition-colors">
                  {{ alert.document_name }}
                </p>
                <!-- Badge -->
                <span
                  class="px-1.5 py-0.2 rounded-full text-[10px] font-extrabold uppercase tracking-wider flex-shrink-0"
                  :class="alert.is_expired ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'"
                >
                  {{ alert.is_expired ? 'Expired' : 'Expiring Soon' }}
                </span>
              </div>

              <!-- Employee Owner Subtext (for Admin/Owner) -->
              <p
                v-if="alert.employee_name && alert.employee_name !== 'My Document'"
                class="text-[11px] text-gray-600 dark:text-gray-300 font-medium truncate mt-0.5 flex items-center gap-1"
              >
                <User class="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span>{{ alert.employee_name }}</span>
                <span v-if="alert.employee_position" class="text-gray-400">• {{ alert.employee_position }}</span>
              </p>

              <!-- Category & Expiry Timing -->
              <div class="flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                <span class="capitalize truncate max-w-[140px]">{{ alert.document_category }}</span>
                <span
                  class="font-semibold whitespace-nowrap"
                  :class="alert.is_expired ? 'text-red-600 dark:text-red-400 font-bold' : 'text-amber-600 dark:text-amber-400'"
                >
                  {{ formatExpiryDescription(alert) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Dropdown Footer Action -->
        <div class="p-2.5 bg-gray-50 dark:bg-gray-850 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
          <button
            type="button"
            class="text-[11px] font-bold text-secondary dark:text-secondary-dark-text hover:underline flex items-center gap-1"
            @click="handleNavigateToDocuments"
          >
            <span>{{ isStaffOnly ? 'View My Documents' : 'Manage Staff Documents' }}</span>
            <ChevronRight class="w-3 h-3" />
          </button>

          <span class="text-[10px] text-gray-400 font-mono">
            {{ store.summary.total_alerts }} total alert{{ store.summary.total_alerts === 1 ? '' : 's' }}
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUser } from '@/composables/auth/useUser'
import { useDocumentNotificationStore } from '@/stores/documentNotification'
import type { DocumentExpirationAlert } from '@/types/documentNotification'
import {
  Bell,
  AlertTriangle,
  Clock,
  CheckCircle2,
  RefreshCw,
  Loader2,
  ChevronRight,
  User,
} from 'lucide-vue-next'

const router = useRouter()
const authStore = useAuthStore()
const { userRoleLabel } = useUser()
const isStaffOnly = computed(() => !authStore.isAdmin)
const store = useDocumentNotificationStore()

const isOpen = ref(false)
const activeFilter = ref<'all' | 'expired' | 'expiring_soon'>('all')
const triggerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    store.fetchAlerts(true)
  }
}

const closeDropdown = () => {
  isOpen.value = false
}

const badgeCount = computed(() => {
  const count = store.summary.total_alerts
  return count > 99 ? '99+' : count.toString()
})

const tooltipText = computed(() => {
  if (store.summary.total_alerts === 0) {
    return 'Document Expiration Alerts: All documents up to date'
  }
  const parts = []
  if (store.summary.expired_count > 0) {
    parts.push(`${store.summary.expired_count} expired`)
  }
  if (store.summary.expiring_soon_count > 0) {
    parts.push(`${store.summary.expiring_soon_count} expiring soon`)
  }
  return `Document Expirations: ${parts.join(', ')}`
})

const filteredAlerts = computed(() => {
  if (activeFilter.value === 'expired') {
    return store.alerts.filter(a => a.is_expired)
  }
  if (activeFilter.value === 'expiring_soon') {
    return store.alerts.filter(a => !a.is_expired)
  }
  return store.alerts
})

const formatExpiryDescription = (alert: DocumentExpirationAlert): string => {
  if (!alert.expiry_date) return 'No expiry'
  const days = alert.days_until_expiry

  if (alert.is_expired) {
    if (days !== null && days < 0) {
      const pastDays = Math.abs(days)
      if (pastDays === 0) return 'Expired today'
      if (pastDays === 1) return 'Expired yesterday'
      if (pastDays < 30) return `Expired ${pastDays}d ago`
      const months = Math.floor(pastDays / 30)
      return `Expired ~${months} mo ago`
    }
    return `Expired (${alert.expiry_date})`
  }

  // Expiring soon
  if (days !== null) {
    if (days === 0) return 'Expires today'
    if (days === 1) return 'Expires tomorrow'
    if (days < 60) return `Expires in ${days}d`
    const months = Math.floor(days / 30)
    return `Expires in ~${months} months`
  }
  return `Expires: ${alert.expiry_date}`
}

const handleRefresh = async () => {
  await store.fetchAlerts(true)
}

const handleAlertClick = (alert: DocumentExpirationAlert) => {
  closeDropdown()
  if (isStaffOnly.value) {
    router.push({ path: '/my-documents', query: { filter: alert.is_expired ? 'expired' : 'expiring_soon' } })
  } else {
    // Admin / Owner
    if (alert.type === 'business_document') {
      router.push('/business-documents')
    } else {
      router.push({
        path: '/staff',
        query: {
          employee_id: alert.employee_id ? alert.employee_id.toString() : undefined,
          open_docs: 'true',
          initial_filter: alert.is_expired ? 'expired' : 'expiring_soon',
        },
      })
    }
  }
}

const handleNavigateToDocuments = () => {
  closeDropdown()
  if (isStaffOnly.value) {
    const query = activeFilter.value !== 'all' ? { filter: activeFilter.value } : undefined
    router.push({ path: '/my-documents', query })
  } else {
    router.push('/staff')
  }
}

// Click outside handling
const handleDocumentClick = (e: MouseEvent) => {
  if (!isOpen.value) return
  const target = e.target as Node
  if (
    triggerRef.value &&
    !triggerRef.value.contains(target) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(target)
  ) {
    closeDropdown()
  }
}

onMounted(() => {
  store.fetchAlerts()
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})
</script>
